import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { makeStyles } from '@material-ui/styles';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles({
  /**
   * Compact chip: staffing-form Alert sized to its label. `inline-flex` + `alignSelf`
   * keep Grid/flex parents from stretching it to 100% width.
   */
  alert: {
    display: 'inline-flex',
    width: 'auto',
    maxWidth: '100%',
    flex: '0 0 auto',
    alignSelf: 'flex-start',
    boxSizing: 'border-box',
    padding: '2px 10px',
    cursor: 'pointer',
    alignItems: 'center',
    '& .MuiAlert-icon': {
      padding: 0,
      marginRight: 8,
      fontSize: 18,
    },
    '& .MuiAlert-message': {
      padding: '6px 0',
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.3,
    },
    // Click leaves the Alert focused (tabIndex=0); do not draw a ring on leftover focus.
    '&:focus, &:focus-visible': {
      outline: 'none',
    },
  },
  // Default error Alert fill is ~8% error.main; a bit more on hover so it reads as clickable.
  error: {
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.16)',
    },
  },
  warning: {
    '&:hover': {
      backgroundColor: 'rgba(255, 152, 0, 0.24)',
    },
  },
});

/**
 * Compact clickable MUI Alert used on project cards and project detail.
 *
 * Stops click bubbling so a project-card header/link does not navigate away before the
 * caller's dialog opens. Enter/Space activate it the same way a button would.
 *
 * @param {object} props
 * @param {'error'|'warning'} props.severity
 * @param {string} [props.className]
 * @param {() => void} props.onClick
 * @param {React.ReactNode} props.children - chip label
 */
const StaffingClickableAlert = ({ severity, className, onClick, children }) => {
  const classes = useStyles();

  /**
   * @param {React.MouseEvent|React.KeyboardEvent} event
   */
  const handleActivate = event => {
    event.stopPropagation();
    event.preventDefault();
    onClick();
  };

  /**
   * @param {React.KeyboardEvent} event
   */
  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleActivate(event);
    }
  };

  return (
    <Alert
      severity={severity}
      className={cx(classes.alert, classes[severity], className)}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-haspopup="dialog"
    >
      {children}
    </Alert>
  );
};

StaffingClickableAlert.propTypes = {
  severity: PropTypes.oneOf(['error', 'warning']).isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default StaffingClickableAlert;
